import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { uccRegistration } from "../../../api/bseApi";
import { FiPlus, FiMinus } from "react-icons/fi";

const TAX_STATUS_OPTIONS = [
  { value: "R", label: "Individual (Resident)" },
  { value: "N", label: "Non-Resident" },
  { value: "P", label: "Partnership" },
  { value: "C", label: "Company" },
  { value: "T", label: "Trust" },
];

const OCCUPATION_CODES = [
  { value: "01", label: "Business" },
  { value: "02", label: "Services" },
  { value: "03", label: "Professional" },
  { value: "04", label: "Agriculture" },
  { value: "05", label: "Retired" },
  { value: "06", label: "Housewife" },
  { value: "07", label: "Student" },
  { value: "08", label: "Others" },
];

const CLIENT_HOLDING_OPTIONS = [
  { value: "SI", label: "Single" },
  { value: "JO", label: "Joint" },
  { value: "AS", label: "Anyone or Survivor" },
];

const IDENTIFICATION_TYPES = [
  { value: "P", label: "PAN" },
  { value: "A", label: "Aadhaar" },
  { value: "V", label: "Voter ID" },
  { value: "D", label: "Driving License" },
  { value: "O", label: "Others" },
];

const DECLARATION_OPTIONS = [
  { value: "SELF", label: "SELF" },
  { value: "GUARDIAN", label: "GUARDIAN" },
  { value: "POA", label: "Power of Attorney" },
];

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const MOBILE_REGEX = /^[0-9]{10}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const initialForm = {
  ClientName: "",
  DOB: "",
  Gender: "",
  MobileNo: "",
  EmailId: "",
  MobileDeclaration: "SELF",
  EmailDeclaration: "SELF",
  OccupationCode: "",
  ApplicantIncome: "",
  PAN: "",
  PANExemptCategory: "",
  CKYCNo: "",
  IdentificationType: "",
  BankName: "",
  AccountNo: "",
  IFSCCode: "",
  AccountType: "",
  ClientHolding: "SI",
  TaxStatus: "R",
  CountryCode: "IN",
  FATCAFlag: "N",
  NomineeName: "",
  NomineeRelation: "",
  NomineePercentage: "",
  NomineeDOB: "",
  ARNCode: "",
  SubBroker: "",
};

const SECTIONS = [
  { id: "personal", title: "Personal Info", defaultOpen: true },
  { id: "investor", title: "Investor Registration", defaultOpen: false },
  { id: "nominee", title: "Nominee Details", defaultOpen: false },
  { id: "bank", title: "Bank Details", defaultOpen: false },
  { id: "fatca", title: "FATCA", defaultOpen: false },
];

/** Accordion section - must be outside main component to prevent re-creation on each keystroke */
function AccordionSection({ id, title, expanded, onToggle, children, actions }) {
  return (
    <div
      className="mb-3"
      style={{
        background: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e6eef0",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => onToggle(id)}
        style={{
          padding: "16px 20px",
          background: expanded ? "#f8fafc" : "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: "600",
          fontSize: "15px",
          color: "#1e293b",
          borderBottom: expanded ? "1px solid #e6eef0" : "none",
        }}
      >
        <span>{title}</span>
        <span style={{ fontSize: "18px", color: "#64748b" }}>
          {expanded ? <FiMinus /> : <FiPlus />}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: "24px" }}>
          {children}
          {actions && (
            <div className="d-flex gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * UccCreateScreen - BSE StAR MF Enhanced UCC Registration
 * Accordion-style form matching BSE portal UX
 */
export default function UccCreateScreen() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [expanded, setExpanded] = useState(
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: s.defaultOpen }), {})
  );

  const toggleSection = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Pre-fill from location state or fetch user
  useEffect(() => {
    const stateUser = location.state?.user;
    if (stateUser) {
      const name = [stateUser.first_name, stateUser.last_name].filter(Boolean).join(" ").trim();
      const mobile = (stateUser.phone || stateUser.mobile || "").replace(/\D/g, "").slice(0, 10);
      const email = stateUser.email || "";
      setForm((prev) => ({
        ...prev,
        ClientName: name || prev.ClientName,
        MobileNo: mobile || prev.MobileNo,
        EmailId: email || prev.EmailId,
      }));
    } else if (userId) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`)
        .then((res) => {
          if (res.data?.data) {
            const u = res.data.data;
            const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
            const mobile = (u.phone || u.mobile || "").replace(/\D/g, "").slice(0, 10);
            setForm((prev) => {
              if (prev.ClientName || prev.MobileNo || prev.EmailId) return prev;
              return {
                ...prev,
                ClientName: name || prev.ClientName,
                MobileNo: mobile || prev.MobileNo,
                EmailId: u.email || prev.EmailId,
                DOB: u.date_of_birth ? u.date_of_birth.split("T")[0] : prev.DOB,
                Gender: u.gender || prev.Gender,
                PAN: (u.pan_number || "").toUpperCase(),
                BankName: u.bank_name || prev.BankName,
                AccountNo: u.account_number || prev.AccountNo,
                IFSCCode: (u.ifsc_code || "").toUpperCase(),
                AccountType: u.account_type || prev.AccountType,
                NomineeName: u.nominee_name || prev.NomineeName,
                NomineeRelation: u.nominee_relationship || prev.NomineeRelation,
                NomineeDOB: u.nominee_dob ? u.nominee_dob.split("T")[0] : prev.NomineeDOB,
              };
            });
          }
        })
        .catch(() => { });
    }
  }, [userId, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "PAN") v = value.toUpperCase();
    if (name === "IFSCCode") v = value.toUpperCase();
    if (name === "FATCAFlag") v = value.toUpperCase();
    if (name === "MobileNo") v = value.replace(/\D/g, "").slice(0, 10);
    if (name === "NomineePercentage") {
      const num = parseInt(value, 10);
      v = isNaN(num) ? "" : Math.min(100, Math.max(0, num)).toString();
    }
    setForm((prev) => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.ClientName?.trim()) err.ClientName = "Required";
    if (!form.DOB?.trim()) err.DOB = "Required";
    if (!form.Gender?.trim()) err.Gender = "Required";
    if (!form.MobileNo?.trim()) err.MobileNo = "Required";
    else if (!MOBILE_REGEX.test(form.MobileNo)) err.MobileNo = "Must be exactly 10 digits";
    if (!form.EmailId?.trim()) err.EmailId = "Required";
    if (!form.PAN?.trim()) err.PAN = "Required";
    else if (!PAN_REGEX.test(form.PAN.toUpperCase())) err.PAN = "Invalid PAN format (e.g. ABCDE1234F)";
    if (!form.BankName?.trim()) err.BankName = "Required";
    if (!form.AccountNo?.trim()) err.AccountNo = "Required";
    if (!form.IFSCCode?.trim()) err.IFSCCode = "Required";
    else if (form.IFSCCode.length !== 11) err.IFSCCode = "IFSC must be exactly 11 characters";
    else if (!IFSC_REGEX.test(form.IFSCCode)) err.IFSCCode = "Invalid IFSC format";
    if (form.NomineePercentage && parseInt(form.NomineePercentage, 10) > 100) {
      err.NomineePercentage = "Must be ≤ 100";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!userId) {
      Swal.fire("Error", "User ID is required.", "error");
      return;
    }
    setLoading(true);
    try {
      const { MobileDeclaration, EmailDeclaration, ARNCode, SubBroker, ...apiFields } = form;
      const payload = { userId: parseInt(userId, 10), ...apiFields };
      const res = await uccRegistration(payload);
      if (res.data?.status === 1) {
        const clientCode = res.data.data?.clientCode;
        const bseFailed = res.data?.bseFailed === true;
        Swal.fire({
          icon: "success",
          title: "UCC Created",
          text: bseFailed
            ? (res.data?.message || "UCC saved locally. You can proceed to FATCA.")
            : `Client Code: ${clientCode || "N/A"}`,
        });
        navigate("/user/fatca", {
          state: {
            clientCode: clientCode || form.PAN,
            PAN: form.PAN,
            clientName: form.ClientName,
            DOB: form.DOB,
            taxStatus: form.TaxStatus,
          },
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || "UCC registration failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        err.message ||
        "UCC registration failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndExit = () => {
    navigate("/user");
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item active">Create UCC</li>
              </ol>
            </nav>
            <h5>Create UCC (BSE StAR MF Client Registration)</h5>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Personal Info */}
            <AccordionSection
              id="personal"
              title="Personal Info"
              expanded={expanded.personal}
              onToggle={toggleSection}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => toggleSection("investor")}
                    className="btn"
                    style={{
                      background: "#10b981",
                      color: "#fff",
                      fontWeight: "600",
                      padding: "10px 24px",
                    }}
                  >
                    Proceed
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAndExit}
                    className="btn btn-secondary"
                    style={{ fontWeight: "600", padding: "10px 24px" }}
                  >
                    Save and exit
                  </button>
                </>
              }
            >
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Tax Status</label>
                  <select
                    className="form-select"
                    name="TaxStatus"
                    value={form.TaxStatus}
                    onChange={handleChange}
                  >
                    {TAX_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">PAN *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.PAN ? "is-invalid" : ""}`}
                    name="PAN"
                    value={form.PAN}
                    onChange={handleChange}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                  {errors.PAN && <div className="invalid-feedback">{errors.PAN}</div>}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.ClientName ? "is-invalid" : ""}`}
                    name="ClientName"
                    value={form.ClientName}
                    onChange={handleChange}
                    placeholder="Full name"
                  />
                  {errors.ClientName && <div className="invalid-feedback">{errors.ClientName}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Date of Birth *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.DOB ? "is-invalid" : ""}`}
                    name="DOB"
                    value={form.DOB}
                    onChange={handleChange}
                  />
                  {errors.DOB && <div className="invalid-feedback">{errors.DOB}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Gender *</label>
                  <div className="d-flex gap-3 pt-2">
                    <label className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        name="Gender"
                        value="M"
                        checked={form.Gender === "M"}
                        onChange={handleChange}
                      />
                      Male
                    </label>
                    <label className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        name="Gender"
                        value="F"
                        checked={form.Gender === "F"}
                        onChange={handleChange}
                      />
                      Female
                    </label>
                  </div>
                  {errors.Gender && <div className="text-danger small">{errors.Gender}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Choose ARN</label>
                  <select
                    className="form-select"
                    name="ARNCode"
                    value={form.ARNCode}
                    onChange={handleChange}
                  >
                    <option value="">Select ARN</option>
                    <option value="ARN-315999">ARN-315999</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Sub Broker</label>
                  <input
                    type="text"
                    className="form-control"
                    name="SubBroker"
                    value={form.SubBroker}
                    onChange={handleChange}
                    placeholder="Type to Search..."
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Mobile *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.MobileNo ? "is-invalid" : ""}`}
                    name="MobileNo"
                    value={form.MobileNo}
                    onChange={handleChange}
                    placeholder="10 digits"
                    maxLength={10}
                  />
                  {errors.MobileNo && <div className="invalid-feedback">{errors.MobileNo}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Mobile Declaration</label>
                  <select
                    className="form-select"
                    name="MobileDeclaration"
                    value={form.MobileDeclaration}
                    onChange={handleChange}
                  >
                    {DECLARATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Email ID *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.EmailId ? "is-invalid" : ""}`}
                    name="EmailId"
                    value={form.EmailId}
                    onChange={handleChange}
                  />
                  {errors.EmailId && <div className="invalid-feedback">{errors.EmailId}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Email Declaration</label>
                  <select
                    className="form-select"
                    name="EmailDeclaration"
                    value={form.EmailDeclaration}
                    onChange={handleChange}
                  >
                    {DECLARATION_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </AccordionSection>

            {/* Investor Registration */}
            <AccordionSection id="investor" title="Investor Registration" expanded={expanded.investor} onToggle={toggleSection}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Occupation Code</label>
                  <select
                    className="form-select"
                    name="OccupationCode"
                    value={form.OccupationCode}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {OCCUPATION_CODES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Applicant Income</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ApplicantIncome"
                    value={form.ApplicantIncome}
                    onChange={handleChange}
                    placeholder="Annual income"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Client Holding</label>
                  <select
                    className="form-select"
                    name="ClientHolding"
                    value={form.ClientHolding}
                    onChange={handleChange}
                  >
                    {CLIENT_HOLDING_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">PAN Exempt Category</label>
                  <input
                    type="text"
                    className="form-control"
                    name="PANExemptCategory"
                    value={form.PANExemptCategory}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">CKYC No</label>
                  <input
                    type="text"
                    className="form-control"
                    name="CKYCNo"
                    value={form.CKYCNo}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Identification Type</label>
                  <select
                    className="form-select"
                    name="IdentificationType"
                    value={form.IdentificationType}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {IDENTIFICATION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </AccordionSection>

            {/* Nominee Details */}
            <AccordionSection id="nominee" title="Nominee Details" expanded={expanded.nominee} onToggle={toggleSection}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Nominee Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="NomineeName"
                    value={form.NomineeName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Nominee Relation</label>
                  <input
                    type="text"
                    className="form-control"
                    name="NomineeRelation"
                    value={form.NomineeRelation}
                    onChange={handleChange}
                    placeholder="e.g. Spouse, Father"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Nominee %</label>
                  <input
                    type="number"
                    className={`form-control ${errors.NomineePercentage ? "is-invalid" : ""}`}
                    name="NomineePercentage"
                    value={form.NomineePercentage}
                    onChange={handleChange}
                    min={0}
                    max={100}
                  />
                  {errors.NomineePercentage && (
                    <div className="invalid-feedback">{errors.NomineePercentage}</div>
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Nominee DOB</label>
                  <input
                    type="date"
                    className="form-control"
                    name="NomineeDOB"
                    value={form.NomineeDOB}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </AccordionSection>

            {/* Bank Details */}
            <AccordionSection id="bank" title="Bank Details" expanded={expanded.bank} onToggle={toggleSection}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Bank Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.BankName ? "is-invalid" : ""}`}
                    name="BankName"
                    value={form.BankName}
                    onChange={handleChange}
                  />
                  {errors.BankName && <div className="invalid-feedback">{errors.BankName}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Account No *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.AccountNo ? "is-invalid" : ""}`}
                    name="AccountNo"
                    value={form.AccountNo}
                    onChange={handleChange}
                  />
                  {errors.AccountNo && <div className="invalid-feedback">{errors.AccountNo}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">IFSC Code *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.IFSCCode ? "is-invalid" : ""}`}
                    name="IFSCCode"
                    value={form.IFSCCode}
                    onChange={handleChange}
                    placeholder="11 characters"
                    maxLength={11}
                  />
                  {errors.IFSCCode && <div className="invalid-feedback">{errors.IFSCCode}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Account Type</label>
                  <input
                    type="text"
                    className="form-control"
                    name="AccountType"
                    value={form.AccountType}
                    onChange={handleChange}
                    placeholder="e.g. Savings, Current"
                  />
                </div>
              </div>
            </AccordionSection>

            {/* FATCA */}
            <AccordionSection id="fatca" title="FATCA" expanded={expanded.fatca} onToggle={toggleSection}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Country Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="CountryCode"
                    value={form.CountryCode}
                    onChange={handleChange}
                    placeholder="IN"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">FATCA Flag</label>
                  <select
                    className="form-select"
                    name="FATCAFlag"
                    value={form.FATCAFlag}
                    onChange={handleChange}
                  >
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                </div>
              </div>
            </AccordionSection>

            <div className="d-flex gap-2 justify-content-end mt-3">
              <button
                type="button"
                onClick={handleSaveAndExit}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Submitting...
                  </>
                ) : (
                  "Submit UCC Registration"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
